import { useEffect, useState, useCallback, MouseEvent } from 'react';
import { useSearchParams } from "react-router-dom";

import { useClientContext, useTranslationContext, useUserContext } from 'hooks';

import { QuestionServiceInstance, TrackerServiceInstance, UserServiceInstance } from 'services';
import * as interfaces from 'interfaces';

import stopWordsData from 'assets/locales/stop_words.json'
import { supportedLanguages } from 'hooks/useTranslationContext.hook';

import Icons from "shared/Icons";
import CounterButton from 'shared/CounterButton';

import classes from './QuestionRecommendations.module.scss';

const stopWords: Record<typeof supportedLanguages[number], string[]> = stopWordsData;

export default function QuestionRecommendations({ newQuestionText, handleClose, promptUserToEnterData, userData }: {
  newQuestionText: string;
  handleClose: () => void;
  promptUserToEnterData: () => void;
  userData: {email: string, firstName: string, zip: string, isUserDataValid: boolean}
}) {

  const { questions, setQuestions, answers } = useClientContext();
  const { user, updateUser, setIsSignedIn } = useUserContext();
  const { t, language } = useTranslationContext();

  const [searchParams, setSearchParams] = useSearchParams();

  const [recommendedQuestions, setRecommendedQuestions]: [interfaces.QuestionInterface[], (c: interfaces.QuestionInterface[]) => void] = useState([] as interfaces.QuestionInterface[]);
  const [recommendedAnswers, setRecommendedAnswers]: [interfaces.AnswerInterface[], (c: interfaces.AnswerInterface[]) => void] = useState([] as interfaces.AnswerInterface[]);

  const getWords = (sentence : string) => sentence.toLowerCase().match(/[\w'-]{2,}/g) || [];
  const matchedWords = (text : string, uniqueWords : string[]) => uniqueWords.filter(word => getWords(text).includes(word)).length;

  const handleLinkCLick = (event: MouseEvent, answerId : string) => {
    event.preventDefault();
    TrackerServiceInstance?.trackEvent('viewSuggestedAnswer', { event_category: 'Answers', answerId: answerId, userId: user.id });
    setSearchParams({ answerId: answerId });
    handleClose();
  };

  const handleUpvote = useCallback(
    (question: interfaces.QuestionInterface) => {
      TrackerServiceInstance?.trackEvent('voteSuggestedQuestion', { event_category: 'Votes', questionId: question.id, userId: user.id });
      QuestionServiceInstance?.voteForQuestion({ questionId: question.id, userId: user.id + '', amount: 1 }).then(
        () => {
          setQuestions(((previousQuestions: interfaces.QuestionInterface[]) =>
            previousQuestions.map((q: interfaces.QuestionInterface) => {
              if (q.id === question.id) {
                return {
                  ...q,
                  votes: q.asked ? q.votes - 1 : q.votes + 1,
                  asked: !q.asked
                };
              }

              return q;
            })) as any);
        }
      );
    },
    [setQuestions]
  );

  const handleNewUserUpvote = (question: interfaces.QuestionInterface) => {
    const { email, firstName, zip, isUserDataValid } = userData;

    if (email && firstName && zip && isUserDataValid) {
      UserServiceInstance?.updateCurrentUserSession({ email, firstName, zip }).then((receivedUser: interfaces.SessionDataInterface) => {
        if (receivedUser.email) {
          updateUser({...user, email: receivedUser.email, zip: receivedUser.zip, firstName: receivedUser.firstName,});
          setIsSignedIn(true);
          handleUpvote(question);
        }
      });
    }
    else
      promptUserToEnterData();
  }

  const getQuestionTranslation = useCallback((question: interfaces.QuestionInterface) => {
    if (language !== question.originalLanguage && question.translations && question.translations[language])
      return question.translations[language];
    return question.text;
    },
    [language]
  );

  useEffect(() => {
    const newQuestionUniqueWords = Array.from(new Set(getWords(newQuestionText))).filter(word => !stopWords[language].includes(word));

    setRecommendedQuestions(questions.filter((question) => matchedWords(getQuestionTranslation(question), newQuestionUniqueWords)));
    setRecommendedAnswers(answers.filter((answer) => matchedWords(getQuestionTranslation(answer.question), newQuestionUniqueWords)));

  }, [questions, answers, newQuestionText, getQuestionTranslation, language]);

  return (
    <div className={classes.QuestionRecommendations}>
      {recommendedQuestions.length + recommendedAnswers.length > 0 &&
        <>
          <label>{t("Suggested Questions")}</label>
          <div className={classes.suggestionsWrapper}>

            {recommendedAnswers.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).map((answer) =>
              <div key={answer.id} className={classes.answer}>
                {getQuestionTranslation(answer.question)}
                <a href={`${window.location.pathname}?answerId=${answer.id}`} onClick={(event) => handleLinkCLick(event, answer.id)}>
                  <Icons iconType="circle-play" iconClass={classes.answerIcon}/>
                </a>
              </div>
            )}

            {recommendedQuestions.map((question) =>
              <div key={question.id} className={classes.question}>
                {getQuestionTranslation(question)}
                <div className={classes.counterButtonWrapper}>
                  <CounterButton
                    buttonCount={question.votes}
                    buttonText={question.asked ? "Upvoted" : "+Upvote"}
                    isButtonActive={question.asked}
                    askButtonColor={'askButtonColor'}
                    handleClick={() => handleUpvote(question)}
                    handleNewUserClick={() => handleNewUserUpvote(question)}
                    hasLikedVideo={false}
                  />
                </div>
              </div>
            )}

          </div>
        </>

      }
    </div>
  );
}
